import React, { useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useRillContext } from "../../../rill/RillReact/share/base";
import CreateBlackoutModal from "./CreateBlackoutModal";
import CreateBlockTimeModal, {
  submitBlockTimePayloads,
} from "./CreateBlockTimeModal";
import { pythonWeekdayToJsGetDay } from "./recurring-weekday-encoding";
import SchedulingEmptyState from "./SchedulingEmptyState";
import { BlackoutRowSkeleton } from "./SchedulingSkeletons";
import { canManageSchedulingRecord } from "./scheduling-event-ownership";

const BLACKOUT_MENU_GAP_PX = 4;

const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Block Time tab: per-event blocks from API (`source: "single_event"`).
 * `action: "single_event"` kept for older payloads.
 */
function isBlockTimeBlackout(b) {
  if (!b || typeof b !== "object") return false;
  return b.source === "single_event" || b.action === "single_event";
}

const SCHEDULE_TYPE_LABEL = {
  single_day: "Single Day",
  date_range: "Date Range",
  recurring: "Recurring",
};

function parseYmdLocal(s) {
  if (!s || typeof s !== "string") return null;
  const day = s.trim().slice(0, 10);
  const m = day.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
}

function formatShortDate(ymd) {
  const d = parseYmdLocal(ymd);
  if (!d) return "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatHmTo12(hm) {
  if (!hm || typeof hm !== "string") return "";
  const m = hm.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return hm;
  const h = Number(m[1]);
  const min = m[2];
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${min} ${ap}`;
}

function formatBlackoutDateDays(b) {
  const st = b.calendar_start_date;
  const en = b.calendar_end_date;
  if (b.schedule_type === "single_day") {
    return st ? formatShortDate(st) : "—";
  }
  if (b.schedule_type === "date_range") {
    if (st && en) return `${formatShortDate(st)} – ${formatShortDate(en)}`;
    if (st) return formatShortDate(st);
    return "—";
  }
  if (b.schedule_type === "recurring") {
    const days = Array.isArray(b.recurring_weekdays)
      ? [...new Set(b.recurring_weekdays.map((d) => Number(d)))]
          .sort(
            (a, b) => pythonWeekdayToJsGetDay(a) - pythonWeekdayToJsGetDay(b)
          )
          .map((py) => DOW_SHORT[pythonWeekdayToJsGetDay(py)])
          .filter(Boolean)
      : [];
    return days.length ? days.join(", ") : "—";
  }
  return "—";
}

function formatBlackoutTimeLabel(b) {
  if (b.time_mode === "full_day") {
    return "Full Day";
  }
  const a = b.time_window_start ?? b.start_time;
  const c = b.time_window_end ?? b.end_time;
  if (a && c) return `${formatHmTo12(a)} – ${formatHmTo12(c)}`;
  if (a) return formatHmTo12(a);
  return "—";
}

function isBlackoutExpired(b) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = b.calendar_end_date ? parseYmdLocal(b.calendar_end_date) : null;
  if (b.schedule_type === "recurring") {
    if (b.recurring_open_ended === true || b.recurring_ends === "never") {
      return false;
    }
    if (end && end < today) return true;
    return false;
  }
  if (end) return end < today;
  const start = b.calendar_start_date
    ? parseYmdLocal(b.calendar_start_date)
    : null;
  if (b.schedule_type === "single_day" && start) return start < today;
  return false;
}

function normalizeBlackoutList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.blackouts)) return payload.blackouts;
  return [];
}

function buildDuplicateBlackoutPayload(raw) {
  const payload = {
    action: "coach_wide",
    name: String(raw?.name ?? raw?.title ?? "").trim(),
    description: String(raw?.description ?? "").trim(),
    schedule_type: raw?.schedule_type,
    time_mode: raw?.time_mode ?? "full_day",
    confirm: true,
  };

  if (raw?.schedule_type === "single_day") {
    payload.single_day = {
      date: raw.single_day?.date ?? raw.calendar_start_date,
    };
  } else if (raw?.schedule_type === "date_range") {
    payload.date_range = {
      start_date: raw.date_range?.start_date ?? raw.calendar_start_date,
      end_date: raw.date_range?.end_date ?? raw.calendar_end_date,
    };
  } else if (raw?.schedule_type === "recurring") {
    payload.recurring_weekdays = Array.isArray(raw.recurring_weekdays)
      ? raw.recurring_weekdays
      : [];
    if (raw.recurring_ends === "never" || raw.recurring_open_ended === true) {
      payload.recurring_ends = "never";
    } else {
      payload.recurring_until_date =
        raw.recurring_until_date ?? raw.calendar_end_date;
    }
  }

  if (raw?.time_mode === "specific_time") {
    payload.start_time = raw.start_time ?? raw.time_window_start;
    payload.end_time = raw.end_time ?? raw.time_window_end;
  }

  return payload;
}

function parseSchedulingClassesPayload(res, json) {
  if (!res.ok || json == null) return [];
  if (Array.isArray(json)) return json;
  if (Array.isArray(json.data)) return json.data;
  if (Array.isArray(json.classes)) return json.classes;
  if (Array.isArray(json.items)) return json.items;
  if (Array.isArray(json.results)) return json.results;
  return [];
}

function parseSchedulingPtPayload(res, json) {
  if (!res.ok || json == null) return [];
  if (Array.isArray(json)) return json;
  if (Array.isArray(json.data)) return json.data;
  if (Array.isArray(json.sessions)) return json.sessions;
  if (Array.isArray(json.items)) return json.items;
  return [];
}

function parseSchedulingFacilitiesPayload(res, json) {
  if (!res.ok || json == null) return [];
  if (Array.isArray(json)) return json;
  if (Array.isArray(json.data)) return json.data;
  if (Array.isArray(json.facilities)) return json.facilities;
  if (Array.isArray(json.items)) return json.items;
  return [];
}

function formatCreatedOnLabel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Match `bookableEventId` to coach classes / PT / facilities for table labels.
 */
function resolveBlockTimeBookableLabels(b, classes, ptSessions, facilities) {
  const rawIds = Array.isArray(b.bookableEventIds)
    ? b.bookableEventIds
    : Array.isArray(b.bookable_event_ids)
      ? b.bookable_event_ids
      : [];
  const ids = rawIds.map((x) => String(x ?? "")).filter(Boolean);
  const singleId = String(b.bookableEventId ?? b.bookable_event_id ?? "");
  if (!ids.length && singleId) ids.push(singleId);

  if (!ids.length) {
    return {
      scheduleType: "—",
      eventNames: [String(b.name ?? "").trim() || "—"],
    };
  }

  const eventNames = [];
  let scheduleType = "—";

  for (const bid of ids) {
    const c = classes.find((x) => String(x._id ?? x.id) === bid);
    if (c) {
      scheduleType = "Classes";
      eventNames.push(String(c.name ?? c.title ?? "").trim() || "—");
      continue;
    }
    const p = ptSessions.find((x) => String(x._id ?? x.id) === bid);
    if (p) {
      scheduleType = "PT Session";
      eventNames.push(String(p.name ?? p.title ?? "").trim() || "—");
      continue;
    }
    const f = facilities.find((x) => String(x._id ?? x.id) === bid);
    if (f) {
      scheduleType = "Facilities";
      eventNames.push(String(f.name ?? f.title ?? "").trim() || "—");
    }
  }

  return {
    scheduleType,
    eventNames: eventNames.length ? eventNames : ["—"],
  };
}

const SchedulingBlackouts = ({ hasNonPullScheduling = false }) => {
  const { locals } = useRillContext();
  const activeUser = locals.activeUser;
  const theme = activeUser.webAppTheme || "dark";
  const [primaryView, setPrimaryView] = React.useState("blackouts");
  const [activeFilter, setActiveFilter] = React.useState("active");
  const [menuOpenId, setMenuOpenId] = React.useState(null);
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [editingBlackout, setEditingBlackout] = React.useState(null);
  const [blackouts, setBlackouts] = React.useState([]);
  const [listLoading, setListLoading] = React.useState(true);
  const [listError, setListError] = React.useState(null);
  const [blackoutFilterOpen, setBlackoutFilterOpen] = React.useState(false);
  const [filterReason, setFilterReason] = React.useState("");
  const [filterDateFrom, setFilterDateFrom] = React.useState("");
  const [filterDateTo, setFilterDateTo] = React.useState("");
  const blackoutFilterRef = React.useRef(null);

  const [blockTimeModalOpen, setBlockTimeModalOpen] = React.useState(false);
  const [blockTimeListsLoading, setBlockTimeListsLoading] =
    React.useState(false);
  const [blockTimeClasses, setBlockTimeClasses] = React.useState([]);
  const [blockTimePtSessions, setBlockTimePtSessions] = React.useState([]);
  const [blockTimeFacilities, setBlockTimeFacilities] = React.useState([]);
  const [menuFixedPos, setMenuFixedPos] = React.useState(null);
  const menuAnchorRef = React.useRef(null);
  const [blockTimeSelection, setBlockTimeSelection] = React.useState({});

  const closeBlackoutMenu = React.useCallback(() => {
    setMenuOpenId(null);
    menuAnchorRef.current = null;
    setMenuFixedPos(null);
  }, []);

  React.useEffect(() => {
    if (!blackoutFilterOpen) return;
    const handler = (e) => {
      if (
        blackoutFilterRef.current &&
        !blackoutFilterRef.current.contains(e.target)
      ) {
        setBlackoutFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [blackoutFilterOpen]);

  const blackoutFilterCount =
    (filterReason.trim() ? 1 : 0) +
    (filterDateFrom ? 1 : 0) +
    (filterDateTo ? 1 : 0);

  const loadBlackouts = React.useCallback(async () => {
    setListError(null);
    setListLoading(true);
    try {
      const res = await fetch("/actions/scheduling/blackouts", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        /* ignore */
      }
      if (!res.ok) {
        const msg =
          (typeof data?.error === "string" && data.error) ||
          `Could not load blackouts (${res.status})`;
        throw new Error(msg);
      }
      setBlackouts(normalizeBlackoutList(data));
    } catch (e) {
      setListError(e?.message ?? "Could not load blackouts");
      setBlackouts([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  const deleteBlackout = React.useCallback(
    async (raw) => {
      const id = raw?._id ?? raw?.id;
      if (!id) return;
      if (!window.confirm("Delete this blackout? This cannot be undone.")) {
        return;
      }
      try {
        const res = await fetch(
          `/actions/scheduling/blackouts/${encodeURIComponent(String(id))}`,
          { method: "DELETE", credentials: "same-origin" }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            (typeof data?.error === "string" && data.error) ||
              (typeof data?.message === "string" && data.message) ||
              `Could not delete blackout (${res.status})`
          );
        }
        closeBlackoutMenu();
        await loadBlackouts();
      } catch (e) {
        window.alert(e?.message ?? "Could not delete blackout");
      }
    },
    [loadBlackouts, closeBlackoutMenu]
  );

  const duplicateBlackout = React.useCallback(
    async (raw) => {
      try {
        const res = await fetch("/actions/scheduling/blackouts", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildDuplicateBlackoutPayload(raw)),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(
            (typeof data?.error === "string" && data.error) ||
              (typeof data?.message === "string" && data.message) ||
              `Could not duplicate blackout (${res.status})`
          );
        }
        closeBlackoutMenu();
        await loadBlackouts();
      } catch (e) {
        window.alert(e?.message ?? "Could not duplicate blackout");
      }
    },
    [loadBlackouts, closeBlackoutMenu]
  );

  useLayoutEffect(() => {
    if (menuOpenId == null) return;
    const update = () => {
      const el = menuAnchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuFixedPos({
        top: rect.bottom + BLACKOUT_MENU_GAP_PX,
        right: window.innerWidth - rect.right,
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [menuOpenId]);

  React.useEffect(() => {
    if (menuOpenId == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeBlackoutMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpenId, closeBlackoutMenu]);

  React.useEffect(() => {
    loadBlackouts();
  }, [loadBlackouts]);

  const loadBlockTimeBookables = React.useCallback(async () => {
    setBlockTimeListsLoading(true);
    setBlockTimeClasses([]);
    setBlockTimePtSessions([]);
    setBlockTimeFacilities([]);
    try {
      const [rc, rpt, rf] = await Promise.all([
        fetch("/actions/scheduling/classes", { credentials: "same-origin" }),
        fetch("/actions/scheduling/pt-sessions", {
          credentials: "same-origin",
        }),
        fetch("/actions/scheduling/facilities", { credentials: "same-origin" }),
      ]);
      const jc = await rc.json().catch(() => null);
      const jpt = await rpt.json().catch(() => null);
      const jf = await rf.json().catch(() => null);
      const own = (list) =>
        list.filter((item) => canManageSchedulingRecord(item, activeUser));
      setBlockTimeClasses(own(parseSchedulingClassesPayload(rc, jc)));
      setBlockTimePtSessions(own(parseSchedulingPtPayload(rpt, jpt)));
      setBlockTimeFacilities(own(parseSchedulingFacilitiesPayload(rf, jf)));
    } catch {
      setBlockTimeClasses([]);
      setBlockTimePtSessions([]);
      setBlockTimeFacilities([]);
    } finally {
      setBlockTimeListsLoading(false);
    }
  }, [activeUser]);

  const openBlockTimeModal = React.useCallback(() => {
    setBlockTimeModalOpen(true);
    loadBlockTimeBookables();
  }, [loadBlockTimeBookables]);

  React.useEffect(() => {
    if (primaryView === "blockTime") {
      loadBlockTimeBookables();
    }
  }, [primaryView, loadBlockTimeBookables]);

  const removeBlockTime = React.useCallback(
    async (raw) => {
      const id = raw?._id ?? raw?.id;
      if (!id) return;
      if (!window.confirm("Remove this block time? This cannot be undone.")) {
        return;
      }
      try {
        const res = await fetch(
          `/actions/scheduling/blackouts/${encodeURIComponent(String(id))}`,
          { method: "DELETE", credentials: "same-origin" }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            (typeof data?.error === "string" && data.error) ||
              (typeof data?.message === "string" && data.message) ||
              `Could not remove block time (${res.status})`
          );
        }
        setMenuOpenId(null);
        await loadBlackouts();
      } catch (e) {
        window.alert(e?.message ?? "Could not remove block time");
      }
    },
    [loadBlackouts]
  );

  const blackoutActiveCount = blackouts.filter(
    (b) => !isBlackoutExpired(b) && !isBlockTimeBlackout(b)
  ).length;

  const blockTimeActiveCount = blackouts.filter(
    (b) => !isBlackoutExpired(b) && isBlockTimeBlackout(b)
  ).length;

  const reasonQuery = filterReason.trim().toLowerCase();
  const filterFromDate = filterDateFrom ? parseYmdLocal(filterDateFrom) : null;
  const filterToDate = filterDateTo ? parseYmdLocal(filterDateTo) : null;
  if (filterToDate) filterToDate.setHours(23, 59, 59, 999);

  const filteredBlackouts = blackouts.filter((b) => {
    if (isBlockTimeBlackout(b)) return false;
    const statusOk =
      activeFilter === "expired" ? isBlackoutExpired(b) : !isBlackoutExpired(b);
    if (!statusOk) return false;
    if (reasonQuery) {
      const name = String(b.name ?? b.title ?? "").toLowerCase();
      const desc = String(b.description ?? "").toLowerCase();
      if (!name.includes(reasonQuery) && !desc.includes(reasonQuery))
        return false;
    }
    if (filterFromDate || filterToDate) {
      const start = b.calendar_start_date
        ? parseYmdLocal(b.calendar_start_date)
        : null;
      const end = b.calendar_end_date
        ? parseYmdLocal(b.calendar_end_date)
        : start;
      const rangeStart = start ?? end;
      const rangeEnd = end ?? start;
      if (rangeStart == null) return false;
      if (filterToDate && rangeStart > filterToDate) return false;
      if (filterFromDate && rangeEnd != null && rangeEnd < filterFromDate)
        return false;
    }
    return true;
  });

  const filteredBlockTimes = blackouts.filter((b) => {
    if (!isBlockTimeBlackout(b)) return false;
    return activeFilter === "expired"
      ? isBlackoutExpired(b)
      : !isBlackoutExpired(b);
  });

  const blockTimeAllChecked =
    filteredBlockTimes.length > 0 &&
    filteredBlockTimes.every((b) =>
      Boolean(blockTimeSelection[String(b._id ?? b.id ?? "")])
    );

  const toggleAllBlockTime = () => {
    const next = !blockTimeAllChecked;
    setBlockTimeSelection(
      Object.fromEntries(
        filteredBlockTimes.map((b) => [String(b._id ?? b.id ?? ""), next])
      )
    );
  };

  const selectedBlockTimeCount = filteredBlockTimes.filter((b) =>
    Boolean(blockTimeSelection[String(b._id ?? b.id ?? "")])
  ).length;

  const deleteSelectedBlockTimes = React.useCallback(async () => {
    const toDelete = filteredBlockTimes.filter((b) =>
      Boolean(blockTimeSelection[String(b._id ?? b.id ?? "")])
    );
    if (!toDelete.length) return;
    if (
      !window.confirm(
        `Remove ${toDelete.length} block time${toDelete.length > 1 ? "s" : ""}? This cannot be undone.`
      )
    )
      return;
    try {
      await Promise.all(
        toDelete.map(async (b) => {
          const id = b._id ?? b.id;
          const res = await fetch(
            `/actions/scheduling/blackouts/${encodeURIComponent(String(id))}`,
            { method: "DELETE", credentials: "same-origin" }
          );
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(
              (typeof data?.error === "string" && data.error) ||
                `Could not remove block time (${res.status})`
            );
          }
        })
      );
      setBlockTimeSelection({});
    } catch (e) {
      window.alert(e?.message ?? "Could not remove some block times");
    } finally {
      await loadBlackouts();
    }
  }, [blockTimeSelection, filteredBlockTimes, loadBlackouts]);

  const rowDisplay = (raw) => ({
    id: String(raw._id ?? raw.id ?? ""),
    title: String(raw.name ?? raw.title ?? "").trim() || "—",
    description: String(raw.description ?? "").trim(),
    typeLabel:
      SCHEDULE_TYPE_LABEL[raw.schedule_type] ?? raw.schedule_type ?? "—",
    dateDaysLabel: formatBlackoutDateDays(raw),
    timeLabel: formatBlackoutTimeLabel(raw),
    status: isBlackoutExpired(raw) ? "expired" : "active",
    raw,
  });

  return (
    <div className="scheduling-blackouts scheduling-classes">
      <nav
        className="scheduling-blackouts-top-tabs"
        aria-label="Blackouts and block time"
      >
        <button
          type="button"
          className="scheduling-classes-tab scheduling-blackouts-top-tab"
          aria-current={primaryView === "blackouts" ? "page" : undefined}
          onClick={() => setPrimaryView("blackouts")}
        >
          Blackouts ({blackoutActiveCount})
        </button>
        <button
          type="button"
          className="scheduling-classes-tab scheduling-blackouts-top-tab"
          aria-current={primaryView === "blockTime" ? "page" : undefined}
          onClick={() => setPrimaryView("blockTime")}
        >
          Block Time ({blockTimeActiveCount})
        </button>
      </nav>

      {primaryView === "blackouts" ? (
        <>
          <header className="scheduling-classes-header scheduling-blackouts-header">
            <hgroup className="scheduling-classes-title-group">
              <h1 className="scheduling-classes-title">Blackouts</h1>
              <p className="scheduling-classes-subtitle">
                Block out dates and times when your services are unavailable.
              </p>
              <div className="scheduling-classes-header-buttons">
                <button
                  type="button"
                  data-button-type="primary"
                  className="scheduling-classes-create-btn"
                  onClick={() => {
                    setEditingBlackout(null);
                    setCreateModalOpen(true);
                  }}
                >
                  + Create Blackouts
                </button>
              </div>
            </hgroup>
          </header>

          <div className="scheduling-classes-box scheduling-blackouts-box">
            <nav className="scheduling-classes-tabs">
              <button
                type="button"
                className="scheduling-classes-tab"
                aria-current={activeFilter === "active" ? "page" : undefined}
                onClick={() => setActiveFilter("active")}
              >
                Active
              </button>
              <button
                type="button"
                className="scheduling-classes-tab"
                aria-current={activeFilter === "expired" ? "page" : undefined}
                onClick={() => setActiveFilter("expired")}
              >
                Expired
              </button>
              <div
                className="scheduling-classes-filter-wrap"
                ref={blackoutFilterRef}
              >
                <button
                  type="button"
                  className="scheduling-classes-filter-btn"
                  aria-expanded={blackoutFilterOpen}
                  disabled={
                    blackouts.filter((b) => !isBlockTimeBlackout(b)).length ===
                    0
                  }
                  onClick={() => setBlackoutFilterOpen((o) => !o)}
                >
                  <img src="/img/new-icons/filter-white.svg" alt="" />
                  Filter
                  {blackoutFilterCount > 0 && (
                    <span className="scheduling-classes-filter-badge">
                      {blackoutFilterCount}
                    </span>
                  )}
                </button>
                {blackoutFilterOpen && (
                  <div
                    className="scheduling-classes-filter-panel"
                    role="dialog"
                  >
                    <label className="scheduling-classes-filter-label">
                      Reason
                      <input
                        type="text"
                        className="scheduling-classes-filter-input"
                        placeholder="Search reason…"
                        value={filterReason}
                        onChange={(e) => setFilterReason(e.target.value)}
                      />
                    </label>
                    <div className="scheduling-classes-filter-row">
                      <label className="scheduling-classes-filter-label">
                        From
                        <input
                          type="date"
                          className="scheduling-classes-filter-input"
                          value={filterDateFrom}
                          onChange={(e) => setFilterDateFrom(e.target.value)}
                        />
                      </label>
                      <label className="scheduling-classes-filter-label">
                        To
                        <input
                          type="date"
                          className="scheduling-classes-filter-input"
                          value={filterDateTo}
                          onChange={(e) => setFilterDateTo(e.target.value)}
                        />
                      </label>
                    </div>
                    <footer className="scheduling-classes-filter-footer">
                      <button
                        type="button"
                        data-button-type="tertiary"
                        onClick={() => {
                          setFilterReason("");
                          setFilterDateFrom("");
                          setFilterDateTo("");
                        }}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        data-button-type="primary"
                        onClick={() => setBlackoutFilterOpen(false)}
                      >
                        Done
                      </button>
                    </footer>
                  </div>
                )}
              </div>
            </nav>

            {listLoading ? (
              <BlackoutRowSkeleton theme={theme} />
            ) : listError ? (
              <p
                className="scheduling-classes-empty scheduling-blackouts-empty"
                role="alert"
              >
                {listError}
              </p>
            ) : filteredBlackouts.length === 0 ? (
              <SchedulingEmptyState
                iconSrc="/img/new-icons/slash.svg"
                title="No blackouts yet"
                description="Add a blackout to close availability on specific days or hours."
                action={
                  <button
                    type="button"
                    data-button-type="primary"
                    onClick={() => setCreateModalOpen(true)}
                  >
                    + Create Blackouts
                  </button>
                }
              />
            ) : (
              <div className="scheduling-blackouts-list-wrap">
                <ul className="scheduling-classes-list scheduling-blackouts-list">
                  {filteredBlackouts.map((apiRow) => {
                    const row = rowDisplay(apiRow);
                    return (
                      <li key={row.id} className="scheduling-blackout-row">
                        <div className="scheduling-blackout-row-check">
                          <input
                            type="checkbox"
                            id={`blackout-${row.id}`}
                            aria-label={`Select ${row.title}`}
                          />
                        </div>
                        <div className="scheduling-blackout-row-lead">
                          <h3 className="scheduling-blackout-row-title">
                            {row.title}
                          </h3>
                          <p className="scheduling-blackout-row-desc">
                            {row.description}
                          </p>
                        </div>
                        <div className="scheduling-blackout-row-col">
                          <span className="scheduling-blackout-row-col-label">
                            Type
                          </span>
                          <span className="scheduling-blackout-row-col-value">
                            {row.typeLabel}
                          </span>
                        </div>
                        <div className="scheduling-blackout-row-col">
                          <span className="scheduling-blackout-row-col-label">
                            Date/Days
                          </span>
                          <span className="scheduling-blackout-row-col-value">
                            {row.dateDaysLabel}
                          </span>
                        </div>
                        <div className="scheduling-blackout-row-col">
                          <span className="scheduling-blackout-row-col-label">
                            Time
                          </span>
                          <span className="scheduling-blackout-row-col-value">
                            {row.timeLabel}
                          </span>
                        </div>
                        <div className="scheduling-blackout-row-actions">
                          <button
                            type="button"
                            data-button-type="outline"
                            onClick={() => {
                              closeBlackoutMenu();
                              setEditingBlackout(row.raw);
                              setCreateModalOpen(true);
                            }}
                          >
                            <img src="/img/new-icons/edit-pencil.svg" alt="" />
                            Edit
                          </button>
                          <div className="scheduling-class-card-menu-wrapper">
                            <button
                              type="button"
                              className="scheduling-class-card-menu-btn"
                              aria-expanded={menuOpenId === row.id}
                              aria-haspopup="true"
                              onClick={(e) => {
                                if (menuOpenId === row.id) {
                                  closeBlackoutMenu();
                                  return;
                                }
                                menuAnchorRef.current = e.currentTarget;
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                setMenuFixedPos({
                                  top: rect.bottom + BLACKOUT_MENU_GAP_PX,
                                  right: window.innerWidth - rect.right,
                                });
                                setMenuOpenId(row.id);
                              }}
                            >
                              <img
                                src="/img/new-icons/more-square.svg"
                                alt="More actions"
                              />
                            </button>
                            {menuOpenId === row.id &&
                              menuFixedPos != null &&
                              createPortal(
                                <>
                                  <div
                                    className="scheduling-class-card-menu-portal-backdrop"
                                    onClick={closeBlackoutMenu}
                                    aria-hidden="true"
                                  />
                                  <ul
                                    className="scheduling-class-card-menu scheduling-class-card-menu--portal"
                                    style={{
                                      top: menuFixedPos.top,
                                      right: menuFixedPos.right,
                                    }}
                                  >
                                    <li>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          duplicateBlackout(row.raw)
                                        }
                                      >
                                        <img
                                          src="/img/new-icons/duplicate.svg"
                                          alt=""
                                        />
                                        Duplicate
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        type="button"
                                        className="scheduling-class-card-menu-delete"
                                        onClick={() => deleteBlackout(row.raw)}
                                      >
                                        <img
                                          src="/img/new-icons/red-trash.svg"
                                          alt=""
                                        />
                                        Delete
                                      </button>
                                    </li>
                                  </ul>
                                </>,
                                document.body
                              )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          <CreateBlackoutModal
            key={
              createModalOpen
                ? String(
                    editingBlackout?._id ?? editingBlackout?.id ?? "create"
                  )
                : "closed"
            }
            isOpen={createModalOpen}
            initialRecord={editingBlackout}
            onClose={() => {
              setCreateModalOpen(false);
              setEditingBlackout(null);
            }}
            onConfirm={async (body) => {
              const blackoutId = editingBlackout?._id ?? editingBlackout?.id;
              const endpoint = blackoutId
                ? `/actions/scheduling/blackouts/${encodeURIComponent(String(blackoutId))}`
                : "/actions/scheduling/blackouts";
              const res = await fetch(endpoint, {
                method: blackoutId ? "PATCH" : "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
              });
              let data = {};
              try {
                data = await res.json();
              } catch {
                /* ignore */
              }
              if (!res.ok) {
                const msg =
                  (typeof data?.error === "string" && data.error) ||
                  (typeof data?.message === "string" && data.message) ||
                  `${blackoutId ? "Could not update" : "Could not create"} blackout (${res.status})`;
                throw new Error(msg);
              }
              await loadBlackouts();
              return data;
            }}
          />
        </>
      ) : (
        <>
          <header className="scheduling-classes-header scheduling-blackouts-header">
            <hgroup className="scheduling-classes-title-group">
              <h1 className="scheduling-classes-title">Block Time</h1>
              <p className="scheduling-classes-subtitle">
                Reserve time on the schedule to prevent bookings during specific
                periods.
              </p>
              <div className="scheduling-classes-header-buttons">
                {selectedBlockTimeCount > 0 ? (
                  <button
                    type="button"
                    className="scheduling-block-time-delete-btn"
                    onClick={deleteSelectedBlockTimes}
                  >
                    <img
                      src="/img/new-icons/vuesax-linear-trash.svg"
                      alt=""
                      aria-hidden="true"
                    />
                    Delete Block time ({selectedBlockTimeCount})
                  </button>
                ) : (
                  <button
                    type="button"
                    data-button-type="primary"
                    className="scheduling-classes-create-btn"
                    onClick={openBlockTimeModal}
                  >
                    + Create Block Time
                  </button>
                )}
              </div>
            </hgroup>
          </header>

          <div className="scheduling-classes-box scheduling-blackouts-box">
            <nav className="scheduling-classes-tabs">
              <button
                type="button"
                className="scheduling-classes-tab"
                aria-current={activeFilter === "active" ? "page" : undefined}
                onClick={() => setActiveFilter("active")}
              >
                Active
              </button>
              <button
                type="button"
                className="scheduling-classes-tab"
                aria-current={activeFilter === "expired" ? "page" : undefined}
                onClick={() => setActiveFilter("expired")}
              >
                Expired
              </button>
              {selectedBlockTimeCount > 0 && (
                <span className="scheduling-block-time-selected-count">
                  {selectedBlockTimeCount} block time selected
                </span>
              )}
            </nav>

            {listLoading ? (
              <BlackoutRowSkeleton theme={theme} />
            ) : listError ? (
              <p
                className="scheduling-classes-empty scheduling-blackouts-empty"
                role="alert"
              >
                {listError}
              </p>
            ) : filteredBlockTimes.length === 0 ? (
              <SchedulingEmptyState
                iconSrc="/img/new-icons/slash.svg"
                title="No block time yet"
                description="Block specific events to remove them from availability."
                action={
                  <button
                    type="button"
                    data-button-type="primary"
                    onClick={() => setBlockTimeModalOpen(true)}
                  >
                    + Create Block Time
                  </button>
                }
              />
            ) : (
              <div className="scheduling-blackouts-list-wrap">
                <div className="scheduling-block-time-table">
                  <div className="scheduling-block-time-header scheduling-block-time-row">
                    <div className="scheduling-blackout-row-check">
                      <input
                        type="checkbox"
                        checked={blockTimeAllChecked}
                        onChange={toggleAllBlockTime}
                        aria-label="Select all block times"
                      />
                    </div>
                    <div className="scheduling-block-time-header-cell">
                      Created on
                      <img
                        src="/img/new-icons/arrows-up-down.svg"
                        className="scheduling-block-time-sort-icon"
                        aria-hidden="true"
                        alt=""
                      />
                    </div>
                    <div className="scheduling-block-time-header-cell">
                      Schedule Type
                      <img
                        src="/img/new-icons/arrows-up-down.svg"
                        className="scheduling-block-time-sort-icon"
                        aria-hidden="true"
                        alt=""
                      />
                    </div>
                    <div className="scheduling-block-time-header-cell">
                      Event
                    </div>
                    <div className="scheduling-block-time-header-cell">
                      Block Date
                      <img
                        src="/img/new-icons/arrows-up-down.svg"
                        className="scheduling-block-time-sort-icon"
                        aria-hidden="true"
                        alt=""
                      />
                    </div>
                    <div className="scheduling-block-time-header-cell">
                      Action
                    </div>
                  </div>
                  <ul className="scheduling-classes-list scheduling-blackouts-list scheduling-block-time-list">
                    {filteredBlockTimes.map((apiRow) => {
                      const rowId = String(apiRow._id ?? apiRow.id ?? "");
                      const { scheduleType, eventNames } =
                        resolveBlockTimeBookableLabels(
                          apiRow,
                          blockTimeClasses,
                          blockTimePtSessions,
                          blockTimeFacilities
                        );
                      const createdOn = formatCreatedOnLabel(
                        apiRow.createdAt ?? apiRow.created
                      );
                      const blockDate = formatBlackoutDateDays(apiRow);
                      return (
                        <li key={rowId} className="scheduling-block-time-row">
                          <div className="scheduling-blackout-row-check">
                            <input
                              type="checkbox"
                              id={`block-time-${rowId}`}
                              checked={Boolean(blockTimeSelection[rowId])}
                              onChange={() =>
                                setBlockTimeSelection((prev) => ({
                                  ...prev,
                                  [rowId]: !prev[rowId],
                                }))
                              }
                              aria-label={`Select block ${eventNames[0]}`}
                            />
                          </div>
                          <span className="scheduling-block-time-cell">
                            {createdOn}
                          </span>
                          <span className="scheduling-block-time-cell">
                            {scheduleType}
                          </span>
                          <div className="scheduling-block-time-event-col">
                            {eventNames.map((name) => (
                              <span
                                key={`${rowId}-${name}`}
                                className="scheduling-block-time-cell"
                              >
                                {name}
                              </span>
                            ))}
                          </div>
                          <span className="scheduling-block-time-cell">
                            {blockDate}
                          </span>
                          <div className="scheduling-blackout-row-actions scheduling-block-time-row-actions">
                            <button
                              type="button"
                              data-button-type="outline"
                              onClick={() => removeBlockTime(apiRow)}
                            >
                              <img
                                src="/img/new-icons/cancel-invite.svg"
                                alt=""
                                aria-hidden="true"
                              />
                              Unblock
                            </button>
                            <button
                              type="button"
                              data-button-type="outline"
                              onClick={() => {}}
                              title="Edit coming soon"
                            >
                              <img
                                src="/img/new-icons/edit-pencil.svg"
                                alt=""
                                aria-hidden="true"
                              />
                              Edit
                            </button>
                            <button
                              type="button"
                              data-button-type="outline"
                              className="scheduling-block-time-delete"
                              onClick={() => removeBlockTime(apiRow)}
                            >
                              <img
                                src="/img/new-icons/red-trash.svg"
                                alt=""
                                aria-hidden="true"
                              />
                              Delete
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
          </div>

          <CreateBlockTimeModal
            isOpen={blockTimeModalOpen}
            listsLoading={blockTimeListsLoading}
            classes={blockTimeClasses}
            ptSessions={blockTimePtSessions}
            facilities={blockTimeFacilities}
            showFacilities={hasNonPullScheduling}
            onClose={() => setBlockTimeModalOpen(false)}
            onConfirm={async (payloads) => {
              await submitBlockTimePayloads(payloads);
              await loadBlackouts();
            }}
          />
        </>
      )}
    </div>
  );
};

export default SchedulingBlackouts;
